import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  Button,
  Divider,
  Form,
  Input,
  Spin,
  Tag,
  Row,
  Col,
  Select,
  Switch,
} from "antd";
import {
  DeviceModelCategory,
  DeviceModelActions,
  EntityStatus,
  tailItemLayout,
  rowLayout,
  colLayout,
} from "../../Constants/Constants";
import { onFormSubmit } from "../../ApiHandlers/DeviceModelApiHandlers";
import { DiscardWarning } from "../Common/DiscardWarning";
import { BreadCrumbs } from "../Common/BreadCrumbs";

const getTitle = (action) => {
  switch (action) {
    case DeviceModelActions.add:
      return "Add";
    case DeviceModelActions.edit:
      return "Update";
    case DeviceModelActions.view:
      return "View";
    default:
      return "";
  }
};

const getInitialValues = (record) => {
  const initialValues = {
    status: EntityStatus.ACTIVE,
  };
  if (record) {
    initialValues.modelNumber = record.modelNumber;
    initialValues.modelName = record.modelName;
    initialValues.manufacturer = record.manufacturer;
    initialValues.manufacturerOui = record.manufacturerOui;
    initialValues.hardwareVersion = record.hardwareVersion;
    initialValues.additionalHardwareVersion = record.additionalHardwareVersion;
    initialValues.productClass = record.productClass;
    initialValues.supportedPlatforms = record.supportedPlatforms;
    initialValues.deviceType = record.deviceType;
    initialValues.category = record.category;
    initialValues.status = record.status;
    initialValues.modelDescription = record.modelDescription;
    initialValues.additionalDetails = record.additionalDetails;
    initialValues.adhocPairing = record.adhocPairing === EntityStatus.Enabled;
    initialValues.partnerCreation =
      record.partnerCreation === EntityStatus.Enabled;
  }
  console.log("initValue", initialValues);
  return initialValues;
};

const DeviceModelForm = (props) => {
  console.log("DeviceModelForm props", props);
  const [form] = Form.useForm();
  const { record, action, onActionClick } = props;

  const isViewForm = action === DeviceModelActions.view;
  const title = useMemo(() => getTitle(action), [action]);
  const [processing, setProcessing] = useState(false);
  const [initialValues, setInitialValues] = useState(getInitialValues(record));

  const onBackClick = () => {
    const isChanged =
      action === DeviceModelActions.view ? false : checkForInputChanges();
    DiscardWarning({ isChanged, onActionClick })();
  };

  const checkForInputChanges = () => {
    const fieldsToCheck = [
      "modelNumber",
      "modelName",
      "manufacturer",
      "manufacturerOui",
      "hardwareVersion",
      "additionalHardwareVersion",
      "category",
      "supportedPlatforms",
      "productClass",
      "deviceType",
      "modelDescription",
      "additionalDetails",
    ];
    const values = form.getFieldsValue();
    let isInputsChanged = false;
    fieldsToCheck.every((f) => {
      if (
        values.hasOwnProperty(f) &&
        values[f] !== initialValues[f] &&
        (!!values[f] || !!initialValues[f])
      ) {
        isInputsChanged = true;
        return false;
      }
      return true;
    });
    return isInputsChanged;
  };
  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(`${title} Device Models`)}
          onParentActionClick={onBackClick}
        />
      </div>
      <Divider className="divider" />
      <div className="form-data-container">
        <fieldset disabled={isViewForm}>
          <Form
            form={form}
            name="device-model-form"
            layout="vertical"
            onFinish={onFormSubmit({ props, setProcessing, setInitialValues })}
            initialValues={initialValues}
            requiredMark={"optional"}
            size={"middle"}
          >
            <Spin spinning={processing} tip={"Processing, please wait..."}>
              <Row {...rowLayout}>
                <Col {...colLayout}>
                  <Form.Item
                    name="modelNumber"
                    label="Model Number"
                    rules={[
                      { required: true, message: "Please fill in this field" },
                    ]}
                  >
                    <Input
                      title="Model Number should be unique"
                      disabled={props.action !== DeviceModelActions.add}
                      style={{ minWidth: "100px", maxWidth: "250px" }}
                    />
                  </Form.Item>
                </Col>
                <Col {...colLayout}>
                  <Form.Item name="modelName" label="Model Name">
                    <Input style={{ minWidth: "150px", maxWidth: "300px" }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row {...rowLayout}>
                <Col {...colLayout}>
                  <Form.Item
                    name="manufacturer"
                    label="Manufacturer"
                    rules={[
                      { required: true, message: "Please fill in this field" },
                    ]}
                  >
                    <Input style={{ minWidth: "150px", maxWidth: "300px" }} />
                  </Form.Item>
                </Col>
                <Col {...colLayout}>
                  <Form.Item
                    name="manufacturerOui"
                    label="Manufacturer OUI"
                    rules={[
                      { required: true, message: "Please fill in this field" },
                    ]}
                  >
                    <Input style={{ minWidth: "150px", maxWidth: "300px" }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row {...rowLayout}>
                <Col {...colLayout}>
                  <Form.Item name="hardwareVersion" label="Hardware Version">
                    <Input style={{ minWidth: "100px", maxWidth: "250px" }} />
                  </Form.Item>
                </Col>
                <Col {...colLayout}>
                  <Form.Item
                    name="additionalHardwareVersion"
                    label="Additional Hardware Version"
                  >
                    <Input style={{ minWidth: "100px", maxWidth: "250px" }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row {...rowLayout}>
                <Col {...colLayout}>
                  <Form.Item
                    name="category"
                    label="Category"
                    rules={[
                      { required: true, message: "Please select category" },
                    ]}
                  >
                    <Select
                      allowClear={true}
                      disabled={props.disabled}
                      placeholder="Select Category"
                      style={{ minWidth: "150px", maxWidth: "200px" }}
                    >
                      {Object.values(DeviceModelCategory).map((item) => (
                        <Select.Option key={item.value}>
                          {item.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="supportedPlatforms"
                    label="Supported Platforms"
                  >
                    <Input style={{ minWidth: "250px", maxWidth: "300px" }} />
                  </Form.Item>
                </Col>
                <Col {...colLayout}>
                  <Form.Item name="productClass" label="Product Class">
                    <div className="display-flex-column">
                      <Input.TextArea
                        autoSize={{ minRows: 4, maxRows: 4 }}
                        style={{ minWidth: "250px", maxWidth: "300px" }}
                      />
                      <label className="input-placeholder">
                        * must be comma separated values
                      </label>
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              <Row {...rowLayout}>
                <Col {...colLayout}>
                  <Form.Item name="deviceType" label="Device Type">
                    <Input style={{ minWidth: "150px", maxWidth: "200px" }} />
                  </Form.Item>
                </Col>
                <Col {...colLayout}>
                  <Form.Item
                    name="status"
                    label="Status:"
                    rules={[{ required: true }]}
                  >
                    <Tag
                      color={
                        initialValues.status === EntityStatus.ACTIVE
                          ? "green"
                          : "red"
                      }
                    >
                      {initialValues.status}
                    </Tag>
                  </Form.Item>
                </Col>
              </Row>

              <Row {...rowLayout}>
                <Col {...colLayout}>
                  <Form.Item name="modelDescription" label="Model Description">
                    <Input.TextArea
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      style={{ minWidth: "250px", maxWidth: "300px" }}
                    />
                  </Form.Item>
                </Col>
                <Col {...colLayout}>
                  <Form.Item
                    name="additionalDetails"
                    label="Additional Details"
                  >
                    <Input.TextArea
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      style={{ minWidth: "250px", maxWidth: "300px" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row {...rowLayout}>
                <Col {...colLayout}>
                  <Form.Item
                    name="adhocPairing"
                    label="Adhoc Pairing :"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren={EntityStatus.Enabled}
                      unCheckedChildren={EntityStatus.Disabled}
                      disabled={isViewForm}
                    />
                  </Form.Item>
                </Col>
                <Col {...colLayout}>
                  <Form.Item
                    name="partnerCreation"
                    label="Partner Creation :"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren={EntityStatus.Enabled}
                      unCheckedChildren={EntityStatus.Disabled}
                      disabled={isViewForm}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {!isViewForm && (
                <Form.Item {...tailItemLayout}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-button"
                  >
                    {title}
                  </Button>
                  <Button
                    htmlType="button"
                    onClick={() => form.resetFields()}
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                </Form.Item>
              )}
            </Spin>
          </Form>
        </fieldset>
      </div>
    </div>
  );
};

DeviceModelForm.propTypes = {
  action: PropTypes.string,
  record: PropTypes.object,
  onSubmit: PropTypes.func,
  parentBread: PropTypes.array,

  deviceModelApi: PropTypes.object,
};

const mapStateToProps = (state) => ({
  deviceModelApi: state.apis.deviceModelApi,
});
const connectedDeviceModelForm = connect(mapStateToProps)(DeviceModelForm);
export default withRouter(connectedDeviceModelForm);
